import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  X, 
  Shield, 
  CheckCircle, 
  Target, 
  BookOpen, 
  TrendingUp, 
  Award, 
  Zap,
  Brain,
  AlertTriangle,
  Lock,
  Eye,
  Activity,
  RefreshCw,
  FileText,
  Settings,
  BarChart3,
  Clock
} from 'lucide-react';
import { formatLastAnalysisDate } from '../utils/dateFormatter';
import { useLexLastSync } from '../contexts/LexLastSyncContext';

interface CadresNISTModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export default function CadresNISTModal({ isOpen, onClose }: CadresNISTModalProps) {
  const [activeTab, setActiveTab] = useState('overview');
  const { dates: lexDates } = useLexLastSync();
  const lastAnalysisDate = isOpen ? (lexDates['cadres_nist_last_ai_analysis'] ?? null) : null;

  if (!isOpen) return null;

  const tabs = [
    { id: 'overview', label: 'Vue d\'ensemble', icon: BookOpen },
    { id: 'csf', label: 'CSF 2.0', icon: Shield },
    { id: 'rmf', label: 'RMF', icon: Target },
    { id: 'integration', label: 'Intégration BZ', icon: Zap },
  ];

  const csfFunctions = [
    {
      id: 'govern',
      name: 'GOVERN',
      description: 'Établir et surveiller la stratégie de cybersécurité, la posture et les programmes',
      icon: Settings,
      color: 'from-purple-500 to-purple-600',
      isNew: true,
      subcategories: [
        'Organizational Context',
        'Risk Management Strategy',
        'Roles & Responsibilities',
        'Policy',
        'Oversight'
      ]
    },
    {
      id: 'identify',
      name: 'IDENTIFY',
      description: 'Développer une compréhension organisationnelle pour gérer les risques',
      icon: Eye,
      color: 'from-blue-500 to-blue-600',
      subcategories: [
        'Asset Management',
        'Business Environment',
        'Governance',
        'Risk Assessment',
        'Risk Management Strategy',
        'Supply Chain Risk Management'
      ]
    },
    {
      id: 'protect',
      name: 'PROTECT',
      description: 'Développer et implémenter des protections pour assurer la délivrance des services critiques',
      icon: Lock,
      color: 'from-green-500 to-green-600',
      subcategories: [
        'Identity Management',
        'Access Control',
        'Awareness & Training',
        'Data Security',
        'Protective Technology',
        'Maintenance'
      ]
    },
    {
      id: 'detect',
      name: 'DETECT',
      description: 'Développer et implémenter des activités pour identifier l\'occurrence d\'un événement',
      icon: Activity,
      color: 'from-yellow-500 to-yellow-600',
      subcategories: [
        'Anomalies & Events',
        'Security Continuous Monitoring',
        'Detection Processes'
      ]
    },
    {
      id: 'respond',
      name: 'RESPOND',
      description: 'Développer et implémenter des activités pour agir face à un événement détecté',
      icon: AlertTriangle,
      color: 'from-orange-500 to-orange-600',
      subcategories: [
        'Response Planning',
        'Communications',
        'Analysis',
        'Mitigation',
        'Improvements'
      ]
    },
    {
      id: 'recover',
      name: 'RECOVER',
      description: 'Développer et implémenter des activités pour maintenir la résilience et restaurer',
      icon: RefreshCw,
      color: 'from-red-500 to-red-600',
      subcategories: [
        'Recovery Planning',
        'Improvements',
        'Communications'
      ]
    }
  ];

  const rmfSteps = [
    {
      id: 'prepare',
      name: 'PREPARE',
      description: 'Établir le contexte organisationnel pour la gestion des risques',
      icon: FileText,
      color: 'from-indigo-500 to-indigo-600',
      activities: [
        'Identifier les objectifs organisationnels',
        'Définir les rôles et responsabilités',
        'Établir les critères de risque',
        'Créer la stratégie de gestion des risques'
      ]
    },
    {
      id: 'categorize',
      name: 'CATEGORIZE',
      description: 'Déterminer l\'impact potentiel des systèmes d\'information',
      icon: BarChart3,
      color: 'from-blue-500 to-blue-600',
      activities: [
        'Identifier les systèmes d\'information',
        'Déterminer l\'impact (CIA)',
        'Classifier les systèmes (Low/Moderate/High)',
        'Documenter les décisions'
      ]
    },
    {
      id: 'select',
      name: 'SELECT',
      description: 'Choisir les contrôles de sécurité appropriés',
      icon: Target,
      color: 'from-green-500 to-green-600',
      activities: [
        'Sélectionner la baseline (NIST SP 800-53)',
        'Adapter les contrôles selon le contexte',
        'Documenter les justifications',
        'Obtenir l\'approbation'
      ]
    },
    {
      id: 'implement',
      name: 'IMPLEMENT',
      description: 'Appliquer les contrôles sélectionnés',
      icon: Settings,
      color: 'from-yellow-500 to-yellow-600',
      activities: [
        'Implémenter les contrôles selon le plan',
        'Documenter l\'implémentation',
        'Former les utilisateurs',
        'Tester les contrôles'
      ]
    },
    {
      id: 'assess',
      name: 'ASSESS',
      description: 'Vérifier l\'efficacité des contrôles mis en place',
      icon: CheckCircle,
      color: 'from-orange-500 to-orange-600',
      activities: [
        'Préparer l\'évaluation',
        'Exécuter l\'évaluation',
        'Analyser les résultats',
        'Documenter les écarts'
      ]
    },
    {
      id: 'authorize',
      name: 'AUTHORIZE',
      description: 'Prendre des décisions formelles concernant l\'acceptation des risques',
      icon: Award,
      color: 'from-purple-500 to-purple-600',
      activities: [
        'Préparer le package d\'autorisation',
        'Réviser les risques résiduels',
        'Prendre la décision (ATO)',
        'Documenter la décision'
      ]
    },
    {
      id: 'monitor',
      name: 'MONITOR',
      description: 'Assurer une surveillance continue des systèmes et des contrôles',
      icon: Activity,
      color: 'from-red-500 to-red-600',
      activities: [
        'Surveiller les contrôles continuellement',
        'Analyser les métriques de sécurité',
        'Répondre aux changements',
        'Réévaluer périodiquement'
      ]
    }
  ];

  return (
    <AnimatePresence>
      {isOpen && (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-4 overflow-y-auto">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.95 }}
            className="bg-gradient-to-br from-slate-900 via-indigo-900 to-slate-900 rounded-2xl max-w-7xl w-full my-8 border border-indigo-500/30 shadow-2xl shadow-indigo-500/20"
          >
            {/* Header */}
            <div className="relative p-8 border-b border-indigo-500/30">
              <div className="flex items-start justify-between">
                <div className="flex items-center gap-4">
                  <div className="p-3 bg-gradient-to-br from-indigo-500 to-purple-500 rounded-xl">
                    <Shield className="w-10 h-10 text-white" />
                  </div>
                  <div>
                    <h2 className="text-4xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-indigo-400 to-purple-400">
                      Cadres NIST - Standards BZ
                    </h2>
                    <p className="text-gray-400 mt-1 text-lg">
                      CSF 2.0 & RMF - Excellence et Conformité Garanties
                    </p>
                    {lastAnalysisDate && (
                      <div className="flex items-center gap-2 mt-2 text-xs text-gray-400">
                        <Clock className="w-3.5 h-3.5" />
                        <span>Dernière analyse AI pour LEX: {formatLastAnalysisDate(lastAnalysisDate)}</span>
                      </div>
                    )}
                  </div>
                  {/* AI Icon */}
                  <motion.div
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: 0.5, type: "spring", stiffness: 200 }}
                    className="flex items-center gap-1 px-3 py-1 backdrop-blur-sm bg-purple-500/40 border border-purple-400/60 rounded-full shadow-lg shadow-purple-500/20 ml-4"
                    title="Contenu analysé et indexé par l'AI Assistant"
                  >
                    <Brain className="w-4 h-4 text-purple-200" />
                    <span className="text-xs text-purple-100 font-semibold">AI</span>
                  </motion.div>
                </div>
                <button
                  onClick={onClose}
                  className="p-2 hover:bg-white/10 rounded-lg transition-colors"
                >
                  <X className="w-6 h-6 text-gray-400" />
                </button>
              </div>
            </div>

            {/* Tabs */}
            <div className="flex overflow-x-auto border-b border-indigo-500/30 bg-slate-900/50">
              {tabs.map((tab) => {
                const Icon = tab.icon;
                return (
                  <button
                    key={tab.id}
                    onClick={() => setActiveTab(tab.id)}
                    className={`flex items-center gap-2 px-6 py-4 font-semibold whitespace-nowrap transition-all ${
                      activeTab === tab.id
                        ? 'bg-gradient-to-r from-indigo-600 to-purple-600 text-white border-b-2 border-indigo-400'
                        : 'text-gray-400 hover:text-white hover:bg-white/5'
                    }`}
                  >
                    <Icon className="w-5 h-5" />
                    {tab.label}
                  </button>
                );
              })}
            </div>

            {/* Content */}
            <div className="p-8 space-y-8 max-h-[calc(100vh-300px)] overflow-y-auto">
              
              {/* TAB: Vue d'ensemble */}
              {activeTab === 'overview' && (
                <div className="space-y-8">
                  <div className="bg-gradient-to-r from-indigo-600/20 via-purple-600/20 to-pink-600/20 rounded-xl p-8 border-2 border-indigo-500/50">
                    <h3 className="text-3xl font-bold text-center text-white mb-4">
                      Les Cadres NIST : Nos Standards de Référence
                    </h3>
                    <p className="text-gray-300 text-lg text-center leading-relaxed mb-4">
                      Chez BZ Inc., nous utilisons <strong className="text-white">rigoureusement</strong> deux cadres NIST comme standards de référence pour <strong className="text-white">toutes</strong> nos livraisons TI et IA.
                    </p>
                    <p className="text-white text-xl text-center font-bold">
                      Ces cadres sont <span className="text-yellow-300">vivants chez BZ</span> et <span className="text-yellow-300">partout</span> dans nos livraisons. Ils ne sont pas optionnels - ils sont <span className="text-red-400">obligatoires</span>.
                    </p>
                  </div>

                  <div className="grid md:grid-cols-2 gap-6">
                    {/* CSF 2.0 Card */}
                    <motion.div
                      initial={{ opacity: 0, x: -20 }}
                      animate={{ opacity: 1, x: 0 }}
                      onClick={() => setActiveTab('csf')}
                      className="bg-gradient-to-br from-blue-500/10 to-cyan-500/10 rounded-xl p-6 border-2 border-blue-500/30 cursor-pointer hover:border-blue-400 transition-all hover:scale-105"
                    >
                      <div className="flex items-center gap-3 mb-4">
                        <div className={`p-3 bg-gradient-to-br ${csfFunctions[0].color} rounded-lg`}>
                          <Shield className="w-8 h-8 text-white" />
                        </div>
                        <div>
                          <h4 className="text-2xl font-bold text-white">NIST CSF 2.0</h4>
                          <p className="text-blue-300 text-sm">Cybersecurity Framework</p>
                        </div>
                      </div>
                      <p className="text-gray-300 mb-4">
                        Standard de référence mondial pour la cybersécurité organisationnelle. <strong className="text-white">6 fonctions</strong>, <strong className="text-white">23 catégories</strong>, <strong className="text-white">108 sous-catégories</strong>.
                      </p>
                      <div className="flex items-center gap-2 text-blue-400 font-semibold">
                        <span>En savoir plus</span>
                        <TrendingUp className="w-4 h-4" />
                      </div>
                    </motion.div>

                    {/* RMF Card */}
                    <motion.div
                      initial={{ opacity: 0, x: 20 }}
                      animate={{ opacity: 1, x: 0 }}
                      onClick={() => setActiveTab('rmf')}
                      className="bg-gradient-to-br from-purple-500/10 to-pink-500/10 rounded-xl p-6 border-2 border-purple-500/30 cursor-pointer hover:border-purple-400 transition-all hover:scale-105"
                    >
                      <div className="flex items-center gap-3 mb-4">
                        <div className="p-3 bg-gradient-to-br from-purple-500 to-pink-500 rounded-lg">
                          <Target className="w-8 h-8 text-white" />
                        </div>
                        <div>
                          <h4 className="text-2xl font-bold text-white">NIST RMF</h4>
                          <p className="text-purple-300 text-sm">Risk Management Framework</p>
                        </div>
                      </div>
                      <p className="text-gray-300 mb-4">
                        Processus structuré de gestion des risques pour systèmes d'information. <strong className="text-white">7 étapes</strong> du cycle de vie complet.
                      </p>
                      <div className="flex items-center gap-2 text-purple-400 font-semibold">
                        <span>En savoir plus</span>
                        <TrendingUp className="w-4 h-4" />
                      </div>
                    </motion.div>
                  </div>

                  {/* Pourquoi c'est important */}
                  <div className="bg-gradient-to-r from-red-600/20 to-orange-600/20 rounded-xl p-6 border-2 border-red-500/50">
                    <h4 className="text-2xl font-bold text-white mb-4 flex items-center gap-2">
                      <AlertTriangle className="w-7 h-7 text-red-400" />
                      Pourquoi c'est OBLIGATOIRE chez BZ?
                    </h4>
                    <ul className="space-y-3 text-gray-300">
                      <li className="flex items-start gap-2">
                        <CheckCircle className="w-5 h-5 text-red-400 flex-shrink-0 mt-0.5" />
                        <span><strong className="text-white">Conformité réglementaire:</strong> Loi 25, RGPD, ISO 27001</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <CheckCircle className="w-5 h-5 text-red-400 flex-shrink-0 mt-0.5" />
                        <span><strong className="text-white">Gestion des risques:</strong> Risques cybersécurité maîtrisés</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <CheckCircle className="w-5 h-5 text-red-400 flex-shrink-0 mt-0.5" />
                        <span><strong className="text-white">Confiance client:</strong> Garantie d'excellence et de rigueur</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <CheckCircle className="w-5 h-5 text-red-400 flex-shrink-0 mt-0.5" />
                        <span><strong className="text-white">Avantage compétitif:</strong> Accès à contrats réglementés</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <CheckCircle className="w-5 h-5 text-red-400 flex-shrink-0 mt-0.5" />
                        <span><strong className="text-white">Responsabilité légale:</strong> Protection en cas d'incident</span>
                      </li>
                    </ul>
                  </div>
                </div>
              )}

              {/* TAB: CSF 2.0 */}
              {activeTab === 'csf' && (
                <div className="space-y-6">
                  <div className="bg-gradient-to-r from-blue-600/20 to-cyan-600/20 rounded-xl p-6 border-2 border-blue-500/50">
                    <h3 className="text-3xl font-bold text-white mb-4">NIST Cybersecurity Framework 2.0</h3>
                    <p className="text-gray-300 text-lg">
                      Publié en <strong className="text-white">février 2024</strong>, le CSF 2.0 est le standard de référence mondial. 
                      Il ajoute une fonction majeure: <strong className="text-yellow-300">GOVERN</strong> (nouveau!).
                    </p>
                  </div>

                  <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {csfFunctions.map((func, idx) => {
                      const Icon = func.icon;
                      return (
                        <motion.div
                          key={func.id}
                          initial={{ opacity: 0, y: 20 }}
                          animate={{ opacity: 1, y: 0 }}
                          transition={{ delay: idx * 0.1 }}
                          className={`bg-gradient-to-br ${func.color}/10 rounded-xl p-6 border-2 border-${func.color.split('-')[1]}-500/30`}
                        >
                          <div className="flex items-center gap-3 mb-3">
                            <div className={`p-2 bg-gradient-to-br ${func.color} rounded-lg`}>
                              <Icon className="w-6 h-6 text-white" />
                            </div>
                            <div>
                              <h4 className="text-xl font-bold text-white">{func.name}</h4>
                              {func.isNew && (
                                <span className="text-xs bg-yellow-500 text-white px-2 py-0.5 rounded-full">NOUVEAU 2.0</span>
                              )}
                            </div>
                          </div>
                          <p className="text-gray-300 text-sm mb-4">{func.description}</p>
                          <div className="space-y-1">
                            <p className="text-xs font-semibold text-gray-400">Sous-catégories:</p>
                            {func.subcategories.map((sub, i) => (
                              <div key={i} className="text-xs text-gray-400">• {sub}</div>
                            ))}
                          </div>
                        </motion.div>
                      );
                    })}
                  </div>

                  <div className="bg-blue-500/10 rounded-xl p-6 border border-blue-500/30">
                    <h4 className="text-xl font-bold text-white mb-3">Évaluation de Maturité</h4>
                    <div className="grid grid-cols-5 gap-2 text-sm">
                      {[1, 2, 3, 4, 5].map((level) => (
                        <div key={level} className="text-center">
                          <div className="text-2xl font-bold text-blue-400">{level}</div>
                          <div className="text-xs text-gray-400">
                            {level === 1 && 'Non existant'}
                            {level === 2 && 'Initialisé'}
                            {level === 3 && 'Défini'}
                            {level === 4 && 'Géré'}
                            {level === 5 && 'Optimisé'}
                          </div>
                        </div>
                      ))}
                    </div>
                    <p className="text-center text-white font-bold mt-4">
                      Objectif BZ: <span className="text-yellow-300">85%+ conformité</span> = organisation mature
                    </p>
                  </div>
                </div>
              )}

              {/* TAB: RMF */}
              {activeTab === 'rmf' && (
                <div className="space-y-6">
                  <div className="bg-gradient-to-r from-purple-600/20 to-pink-600/20 rounded-xl p-6 border-2 border-purple-500/50">
                    <h3 className="text-3xl font-bold text-white mb-4">NIST Risk Management Framework</h3>
                    <p className="text-gray-300 text-lg">
                      Processus structuré pour intégrer la gestion de la sécurité dans le cycle de vie des systèmes.
                      <strong className="text-white"> 7 étapes</strong> du cycle de vie complet.
                    </p>
                  </div>

                  <div className="space-y-4">
                    {rmfSteps.map((step, idx) => {
                      const Icon = step.icon;
                      return (
                        <motion.div
                          key={step.id}
                          initial={{ opacity: 0, x: -20 }}
                          animate={{ opacity: 1, x: 0 }}
                          transition={{ delay: idx * 0.1 }}
                          className={`bg-gradient-to-br ${step.color}/10 rounded-xl p-6 border-2 border-${step.color.split('-')[1]}-500/30`}
                        >
                          <div className="flex items-start gap-4">
                            <div className={`p-3 bg-gradient-to-br ${step.color} rounded-lg flex-shrink-0`}>
                              <Icon className="w-8 h-8 text-white" />
                            </div>
                            <div className="flex-1">
                              <div className="flex items-center gap-3 mb-2">
                                <h4 className="text-2xl font-bold text-white">{step.name}</h4>
                                <span className="text-sm bg-purple-500/30 text-purple-200 px-2 py-1 rounded-full">
                                  Étape {idx + 1}/7
                                </span>
                              </div>
                              <p className="text-gray-300 mb-4">{step.description}</p>
                              <div className="space-y-2">
                                <p className="text-sm font-semibold text-gray-400">Activités principales:</p>
                                <ul className="space-y-1">
                                  {step.activities.map((activity, i) => (
                                    <li key={i} className="text-sm text-gray-300 flex items-start gap-2">
                                      <CheckCircle className="w-4 h-4 text-purple-400 flex-shrink-0 mt-0.5" />
                                      {activity}
                                    </li>
                                  ))}
                                </ul>
                              </div>
                            </div>
                          </div>
                        </motion.div>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* TAB: Intégration BZ */}
              {activeTab === 'integration' && (
                <div className="space-y-6">
                  <div className="bg-gradient-to-r from-green-600/20 to-emerald-600/20 rounded-xl p-6 border-2 border-green-500/50">
                    <h3 className="text-3xl font-bold text-white mb-4">Intégration chez BZ Inc.</h3>
                    <p className="text-gray-300 text-lg">
                      Comment BZ utilise <strong className="text-white">rigoureusement</strong> ces cadres dans toutes nos livraisons.
                    </p>
                  </div>

                  <div className="grid md:grid-cols-2 gap-6">
                    <div className="bg-blue-500/10 rounded-xl p-6 border border-blue-500/30">
                      <h4 className="text-xl font-bold text-white mb-4 flex items-center gap-2">
                        <Shield className="w-6 h-6 text-blue-400" />
                        Livraisons TI
                      </h4>
                      <ul className="space-y-2 text-gray-300">
                        <li className="flex items-start gap-2">
                          <CheckCircle className="w-5 h-5 text-blue-400 flex-shrink-0 mt-0.5" />
                          <span>Chaque système évalué selon NIST CSF 2.0</span>
                        </li>
                        <li className="flex items-start gap-2">
                          <CheckCircle className="w-5 h-5 text-blue-400 flex-shrink-0 mt-0.5" />
                          <span>Chaque nouveau système suit le processus RMF</span>
                        </li>
                        <li className="flex items-start gap-2">
                          <CheckCircle className="w-5 h-5 text-blue-400 flex-shrink-0 mt-0.5" />
                          <span>Tous les contrôles alignés avec NIST SP 800-53</span>
                        </li>
                        <li className="flex items-start gap-2">
                          <CheckCircle className="w-5 h-5 text-blue-400 flex-shrink-0 mt-0.5" />
                          <span>Plans d'incident suivent RESPOND/RECOVER</span>
                        </li>
                      </ul>
                    </div>

                    <div className="bg-purple-500/10 rounded-xl p-6 border border-purple-500/30">
                      <h4 className="text-xl font-bold text-white mb-4 flex items-center gap-2">
                        <Brain className="w-6 h-6 text-purple-400" />
                        Livraisons IA
                      </h4>
                      <ul className="space-y-2 text-gray-300">
                        <li className="flex items-start gap-2">
                          <CheckCircle className="w-5 h-5 text-purple-400 flex-shrink-0 mt-0.5" />
                          <span>IA traitée comme système critique (RMF)</span>
                        </li>
                        <li className="flex items-start gap-2">
                          <CheckCircle className="w-5 h-5 text-purple-400 flex-shrink-0 mt-0.5" />
                          <span>Risques IA évalués selon NIST AI Framework</span>
                        </li>
                        <li className="flex items-start gap-2">
                          <CheckCircle className="w-5 h-5 text-purple-400 flex-shrink-0 mt-0.5" />
                          <span>Gouvernance IA suit GOVERN (CSF 2.0)</span>
                        </li>
                        <li className="flex items-start gap-2">
                          <CheckCircle className="w-5 h-5 text-purple-400 flex-shrink-0 mt-0.5" />
                          <span>Protection données IA suit PROTECT</span>
                        </li>
                      </ul>
                    </div>
                  </div>

                  <div className="bg-red-500/10 rounded-xl p-6 border-2 border-red-500/50">
                    <h4 className="text-2xl font-bold text-white mb-4 flex items-center gap-2">
                      <AlertTriangle className="w-7 h-7 text-red-400" />
                      Conséquences du Non-Respect
                    </h4>
                    <div className="grid md:grid-cols-2 gap-4">
                      <div>
                        <p className="text-red-300 font-semibold mb-2">❌ Si on ne respecte PAS:</p>
                        <ul className="space-y-1 text-gray-300 text-sm">
                          <li>• Non-conformité réglementaire</li>
                          <li>• Risques non gérés</li>
                          <li>• Perte de confiance client</li>
                          <li>• Responsabilité légale</li>
                          <li>• Exclusion de contrats</li>
                        </ul>
                      </div>
                      <div>
                        <p className="text-green-300 font-semibold mb-2">✅ Si on respecte RIGOUREUSEMENT:</p>
                        <ul className="space-y-1 text-gray-300 text-sm">
                          <li>• Conformité garantie</li>
                          <li>• Risques maîtrisés</li>
                          <li>• Confiance client renforcée</li>
                          <li>• Avantage compétitif</li>
                          <li>• Accès contrats réglementés</li>
                        </ul>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Footer */}
            <div className="p-6 border-t border-indigo-500/30 bg-slate-900/50">
              <div className="flex items-center justify-between">
                <p className="text-gray-400 text-sm">
                  Cadres NIST • Standards BZ • Excellence Garantie
                </p>
                <button
                  onClick={onClose}
                  className="px-6 py-3 bg-gradient-to-r from-indigo-500 to-purple-500 text-white rounded-xl hover:from-indigo-600 hover:to-purple-600 transition-all duration-200 font-semibold shadow-lg shadow-indigo-500/20 hover:shadow-indigo-500/40 hover:scale-105"
                >
                  Fermer
                </button>
              </div>
            </div>
          </motion.div>
        </div>
      )}
    </AnimatePresence>
  );
}

